import type { Exam } from '../../../../../db/exam.js'; // Pfad ggf. anpassen
import { awardedPoints, computeAggregate } from '../../../../../db/utils/points-model.js';
import type { GradingScale } from '../../../../../shared/grading-scales.js';
import type { GrammarFiSettings } from '../../../../../shared/grammar-grading.js';
import {
  getSoloLanguageUseRule,
  resolveSoloLanguageUseMaxPoints,
} from '../../../../../shared/grammar-rules-resolution.js';

export interface SummaryGradingResult {
  score?: number;
  actualPointsGiven?: number;
  explanation?: string;
  confidence?: string | number;
  isApproved?: boolean;
}

/** One shared per-group grading result, matched to a LanguageUseScheme by languageUseSchemeId. */
export interface SummaryLanguageUseRuleResult {
  languageUseSchemeId: string;
  score: number;
  explanation: string;
  confidence: number;
  isApproved?: boolean;
  actualPointsGiven?: number;
}

export interface SummaryBoundingBox {
  confidence?: string | number;
  approved?: boolean;
}

export interface SummaryGrammarEvaluation {
  errorCount?: number;
  wordCount?: number;
  fehlerindex?: number;
}

export interface SummarySubTask {
  _id?: string;
  taskNumber?: string;
  gradingResult?: SummaryGradingResult;
  boundingBoxes?: SummaryBoundingBox[];
  grammarEvaluation?: SummaryGrammarEvaluation;
  subTasksSubmission?: SummarySubTask[];
}

export interface SummaryTask {
  _id?: string;
  taskNumber?: string;
  gradingState?: string;
  gradingResult?: SummaryGradingResult;
  boundingBoxes?: SummaryBoundingBox[];
  grammarEvaluation?: SummaryGrammarEvaluation;
  subTasksSubmission?: SummarySubTask[];
}

export interface SummarySubmission {
  _id: string;
  studentName?: string;
  taskSubmissions?: SummaryTask[];
  languageUseRuleResults?: SummaryLanguageUseRuleResult[];
}

export interface TaskDefinition {
  taskKey: string;
  label: string;
  maxPoints: number;
  /** Own-criteria language-use max points across this task's subtree — display value only. Can be 0
   * even when a language-use rule genuinely exists (e.g. the agent failed to set maxPoints), so gate
   * the companion "· Sprachliche Leistung" column's visibility on hasOwnLanguageUseCriteria below, not
   * on this number. */
  languageUseMaxPoints: number;
  /** Whether any node in this task's subtree has its own merged languageUseCriteria text at all,
   * independent of languageUseMaxPoints — the correct signal for whether to show the "· Sprachliche
   * Leistung" column/row, since maxPoints alone can't distinguish "no rule" from "rule with broken
   * points". */
  hasOwnLanguageUseCriteria: boolean;
}

/** One column per distinct multi-task LanguageUseScheme group (Part C2), independent of the task tree. */
export interface LanguageUseGroupColumn {
  schemeId: string;
  label: string;
  maxPoints: number;
  dependsOn: string[];
}

export interface StudentSummary {
  id: string;
  submissionIndex: number;
  name: string;
  pointsByTask: Record<string, number>;
  /** Own-criteria language-use points earned per task (Part C1 split), keyed by taskKey. Only
   * meaningful where the matching TaskDefinition.hasOwnLanguageUseCriteria is true. */
  ownLanguageUsePointsByTask: Record<string, number>;
  /** Own-criteria language-use confidence/approval per task (worst across the task's subtree), keyed
   * by taskKey — feeds the "· Sprachliche Leistung" column's ConfidenceIndicator, mirroring
   * confidenceByTask. */
  ownLanguageUseConfidenceByTask: Record<string, number | null>;
  ownLanguageUseIsApprovedByTask: Record<string, boolean>;
  gradingByTask: Record<string, string>;
  confidenceByTask: Record<string, number | null>;
  isApprovedByTask: Record<string, boolean>;
  totalPoints: number;
  totalMaxPoints: number;
  percentage: number;
  /** Final grade including a possible Fehlerindex deduction (equals baseGrade when none applies). */
  grade: string;
  /** Pure points-derived grade before any Fehlerindex deduction. */
  baseGrade: string;
  /** Grade steps deducted via the Fehlerindex (0 when FI is off or in rules mode). */
  fiDeductionSteps: number;
  /** Hypothetical grade if FI applied right now, across ALL grammar-eligible tasks (simulating the
   * switch being flipped on) — shown in the Language Use tab regardless of whether FI is actually active. */
  gradeWithFiApplied: string;
  /** Per-task grammar aggregates keyed by taskKey (null = task has no grammar evaluation), across
   * ALL grammar-eligible tasks regardless of FI vs. rules mode — feeds the Language Use tab matrix. */
  grammarByTask: Record<string, { errorCount: number; wordCount: number; fehlerindex: number } | null>;
  /** Same shape as grammarByTask, but scoped to still-FI-mode tasks only — feeds grammarErrorCount/
   * grammarFehlerindex below and the legacy Schülerübersicht "Grammatik" column; never rules-graded. */
  fiGrammarByTask: Record<string, { errorCount: number; wordCount: number; fehlerindex: number } | null>;
  /** Total grammar/spelling errors across grammar-evaluated tasks (null = no grammar eval). */
  grammarErrorCount: number | null;
  /** Aggregate Fehlerindex (errors per 100 words) across grammar-evaluated tasks. */
  grammarFehlerindex: number | null;
  /** taskKey of the first task that has a grammar evaluation (null = none). */
  firstGrammarTaskKey: string | null;
  /** Earned points per multi-task LanguageUseScheme group (Part C2), keyed by LanguageUseScheme._id. */
  languageUseGroupPointsBySchemeId: Record<string, number>;
  /** Confidence/approval of this submission's shared per-group language-use result, keyed by
   * LanguageUseScheme._id — feeds the group column's ConfidenceIndicator. null = not yet graded. */
  languageUseGroupConfidenceBySchemeId: Record<string, number | null>;
  languageUseGroupIsApprovedBySchemeId: Record<string, boolean>;
}

export interface CorrectionSummaryProps {
  submissions: SummarySubmission[];
  exam: Exam | null;
  onSaveUpdate?: (
    submissionId: string,
    mainTaskId: string,
    subTaskId: string | null,
    boxId: string | null,
    updatedData: {
      extractedText?: string;
      gradingText?: string;
      score?: number;
      isApproved?: boolean;
      boxApproved?: boolean;
      boxCoordinates?: {
        x?: number;
        y?: number;
        width?: number;
        height?: number;
        pageNumber?: number;
      };
      addBoundingBox?: boolean;
      deleteBoundingBox?: boolean;
    },
  ) => Promise<void>;
  onDownloadReports?: () => void | Promise<void>;
  isDownloadingReports?: boolean;
  canDownloadReports?: boolean;
  downloadDisabledReason?: string;
  onDownloadGradebookCsv?: () => void | Promise<void>;
  isDownloadingGradebookCsv?: boolean;
  canDownloadGradebookCsv?: boolean;
  gradebookCsvDisabledReason?: string;
  onNavigateToTask?: (
    submissionIdx: number,
    taskKey: string,
    mode?: 'correction' | 'grammar',
    boxMode?: 'content' | 'languageUse',
  ) => void;
  onSaveGradingScale?: (scale: GradingScale) => Promise<void>;
  onSaveGrammarFiSettings?: (settings: GrammarFiSettings) => Promise<void>;
  /** Teacher's default FI choice (Teacher.grammarPreferences.alwaysUseFi) — see resolveAlwaysUseFi.
   * The exam-level override lives in state.grammarFiSettings.alwaysUseFi instead (parsed from
   * Exam.grammarFiSettingsJson and kept optimistically in sync — see use-summary-state.ts's forceFi),
   * so it isn't a separate prop here. */
  teacherAlwaysUseFi?: boolean | null;
}

export type SummaryTabKey = 'students' | 'clarification' | 'insights' | 'grades' | 'curving' | 'languageUse' | 'export';
export type ClarificationSortField = 'student' | 'task' | 'uncertainty';
export type ClarificationSortDirection = 'asc' | 'desc';

export interface TaskStatusSummary {
  total: number;
  finished: number;
  processing: number;
  pending: number;
  error: number;
  isComplete: boolean;
}

export interface ClarificationCase {
  id: string;
  submissionIndex: number;
  submissionId: string;
  studentName: string;
  taskKey: string;
  taskLabel: string;
  taskPath: string[];
  depth: number;
  mainTaskId: string | null;
  subTaskId: string | null;
  confidence: number;
  points: number;
  maxPoints: number;
  reason: string;
  /** Which per-box selector to land on when navigating — 'languageUse' when this case's only open
   * issue is language-use-side (content is fine), so the teacher isn't dropped onto an unrelated view. */
  boxMode: 'content' | 'languageUse';
}

export const SUMMARY_TABS: Array<{ key: SummaryTabKey; labelKey: string }> = [
  { key: 'students', labelKey: 'Canvas.summary.tabs.students' },
  { key: 'clarification', labelKey: 'Canvas.summary.tabs.clarification' },
  { key: 'insights', labelKey: 'Canvas.summary.tabs.insights' },
  { key: 'grades', labelKey: 'Canvas.summary.tabs.grades' },
  { key: 'curving', labelKey: 'Canvas.summary.tabs.curving' },
  { key: 'languageUse', labelKey: 'Canvas.summary.tabs.languageUse' },
  { key: 'export', labelKey: 'Canvas.summary.tabs.export' },
];

/** A student only ever has their own single submission — class-wide/administrative tabs
 * (Aufgaben-Insights, Notenspiegel, Curving, Export) are hidden for them. */
export const STUDENT_VISIBLE_SUMMARY_TAB_KEYS: SummaryTabKey[] = ['students', 'clarification', 'languageUse'];

export function isTaskFinishedByState(gradingState: unknown): boolean {
  return gradingState === 'COMPLETED' || gradingState === 'ERROR';
}

export function unwrapDoc<T>(value: T | { doc?: T } | undefined): T | undefined {
  if (!value) return undefined;
  if (typeof value === 'object' && value !== null && 'doc' in (value as any)) {
    return (value as any).doc as T | undefined;
  }
  return value as T;
}

export function parseNumber(value: unknown): number | null {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value.replace(',', '.').replace(/[^0-9.\-]/g, ''))
        : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

export function resolveSummaryUnresolvedConfidence(
  gradingResult: SummaryGradingResult | undefined,
  boxes: SummaryBoundingBox[] | undefined,
): number | null {
  if (gradingResult?.isApproved === true) return null;

  const candidates: number[] = [];

  const gradingConfidence = parseNumber(gradingResult?.confidence);
  if (gradingConfidence != null) candidates.push(gradingConfidence);

  if (Array.isArray(boxes)) {
    for (const box of boxes) {
      if (box?.approved === true) continue;
      const boxConfidence = parseNumber(box?.confidence);
      if (boxConfidence != null) candidates.push(boxConfidence);
    }
  }

  if (candidates.length === 0) return null;
  return Math.min(...candidates);
}

export function resolveAchievedPoints(gradingResult: SummaryGradingResult | undefined, maxPoints: number): number {
  // Awarded points from the single source of truth (actualPointsGiven → score → 0), clamped to the max.
  return Math.max(0, Math.min(awardedPoints({ gradingResult }), maxPoints));
}

export function resolveMaxPoints(task: any): number {
  // Whole-subtree total from the single source of truth (own + children, fixed/dynamic aware), 0 when unpointed.
  return computeAggregate(task);
}

/** Solo-rule language-use max points across a task's whole subtree — shown in its own "· Sprachliche
 * Leistung" summary column instead of folded into the task's own points (kept separate, unlike group
 * columns which were always separate). 0 when no node in the subtree carries a solo language-use rule. */
export function resolveSoloLanguageUseMaxPointsForTask(task: any, languageUseSchemeRules: any[]): number {
  const resolveNodeLanguageUseMaxPoints = (node: any, path: string[]): number => {
    if (!node) return 0;

    const needsAnswer = node?.needsAnswer !== false;
    const ownLanguageUseContribution = needsAnswer
      ? (resolveSoloLanguageUseMaxPoints(path.join('|'), languageUseSchemeRules) ?? 0)
      : 0;

    const children = Array.isArray(node?.subTasks) ? node.subTasks : [];
    const childrenContribution = children.reduce(
      (sum: number, child: any) =>
        sum + resolveNodeLanguageUseMaxPoints(child, [...path, String(child?.taskNumber ?? '')]),
      0,
    );

    return ownLanguageUseContribution + childrenContribution;
  };

  return resolveNodeLanguageUseMaxPoints(task, [String(task?.taskNumber ?? '')]);
}

/** Whether any node in a task's subtree carries a solo (1-member) language-use rule, independent of
 * languageUseMaxPoints (which can be 0 even when criteria genuinely exists, e.g. a broken extraction)
 * — the correct gate for the "· Sprachliche Leistung" column/row's visibility. */
export function taskHasSoloLanguageUseCriteria(task: any, languageUseSchemeRules: any[]): boolean {
  const check = (node: any, path: string[]): boolean => {
    if (!node) return false;
    const needsAnswer = node?.needsAnswer !== false;
    if (needsAnswer && getSoloLanguageUseRule(path.join('|'), languageUseSchemeRules)) return true;

    const children = Array.isArray(node?.subTasks) ? node.subTasks : [];
    return children.some((child: any) => check(child, [...path, String(child?.taskNumber ?? '')]));
  };
  return check(task, [String(task?.taskNumber ?? '')]);
}

export function roundToOne(value: number): number {
  return Math.round(value * 10) / 10;
}

export function normalizeExplanation(text: unknown): string {
  if (typeof text !== 'string') return '';
  return text.replace(/\s+/g, ' ').trim();
}

export function getGradeCardClasses(averagePercentage: number) {
  if (averagePercentage >= 80)
    return { card: 'bg-green-50 border-green-200', label: 'text-green-700', value: 'text-green-800' };
  if (averagePercentage >= 60)
    return { card: 'bg-amber-50 border-amber-200', label: 'text-amber-700', value: 'text-amber-800' };
  return { card: 'bg-red-50 border-red-200', label: 'text-red-700', value: 'text-red-800' };
}

export function getPointsCardClasses(averagePercentage: number) {
  if (averagePercentage >= 80)
    return { card: 'bg-green-50 border-green-200', label: 'text-green-700', value: 'text-green-800' };
  if (averagePercentage >= 60)
    return { card: 'bg-amber-50 border-amber-200', label: 'text-amber-700', value: 'text-amber-800' };
  return { card: 'bg-red-50 border-red-200', label: 'text-red-700', value: 'text-red-800' };
}
